'use client';

import React from 'react';
import {
  Search, Loader2, Building2, Package, AlertTriangle,
} from 'lucide-react';
import type { Customer, Job } from './useCustomerJobSearch';

interface CustomerJobSelectorProps {
  selectedCustomer: { id: number; company_name: string | null; contact_person: string | null } | null;
  setSelectedCustomer: (c: { id: number; company_name: string | null; contact_person: string | null } | null) => void;
  customerSearchQuery: string;
  setCustomerSearchQuery: (q: string) => void;
  showCustomerSearch: boolean;
  setShowCustomerSearch: (show: boolean) => void;
  loadingCustomers: boolean;
  filteredCustomers: Customer[];
  customerDropdownRef: React.RefObject<HTMLDivElement | null>;
  selectedJob: { id: string; item_number: string; title: string; contact_name?: string | null } | null;
  setSelectedJob: (j: { id: string; item_number: string; title: string; contact_name?: string | null } | null) => void;
  jobSearchQuery: string;
  setJobSearchQuery: (q: string) => void;
  showJobSearch: boolean;
  setShowJobSearch: (show: boolean) => void;
  loadingJobs: boolean;
  jobsChecked: boolean;
  filteredJobs: Job[];
  activeJobs: Job[];
  jobDropdownRef: React.RefObject<HTMLDivElement | null>;
}

export function CustomerJobSelector({
  selectedCustomer,
  setSelectedCustomer,
  customerSearchQuery,
  setCustomerSearchQuery,
  showCustomerSearch,
  setShowCustomerSearch,
  loadingCustomers,
  filteredCustomers,
  customerDropdownRef,
  selectedJob,
  setSelectedJob,
  jobSearchQuery,
  setJobSearchQuery,
  showJobSearch,
  setShowJobSearch,
  loadingJobs,
  jobsChecked,
  filteredJobs,
  activeJobs,
  jobDropdownRef,
}: CustomerJobSelectorProps) {
  return (
    <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
      {/* Customer Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Customer *
        </label>
        {selectedCustomer ? (
          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-gray-400" />
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {selectedCustomer.company_name || selectedCustomer.contact_person || 'Unnamed'}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedCustomer(null);
                setSelectedJob(null);
                setCustomerSearchQuery('');
                setJobSearchQuery('');
              }}
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="relative" ref={customerDropdownRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search customers..."
                value={customerSearchQuery}
                onChange={(e) => {
                  setCustomerSearchQuery(e.target.value);
                  setShowCustomerSearch(true);
                }}
                onFocus={() => setShowCustomerSearch(true)}
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
            </div>
            {showCustomerSearch && (
              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                {loadingCustomers ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                    Loading customers...
                  </div>
                ) : filteredCustomers.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    {customerSearchQuery.trim() ? 'No customers found' : 'No customers available'}
                  </div>
                ) : (
                  <div>
                    {filteredCustomers.map((customer) => (
                      <button
                        key={customer.id}
                        type="button"
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setShowCustomerSearch(false);
                          setCustomerSearchQuery('');
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {customer.company_name || customer.contact_person || 'Unnamed'}
                        </div>
                        {customer.email && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">{customer.email}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Active Job Selector (only after customer selected) */}
      {selectedCustomer && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Active Job *
          </label>
          {selectedJob ? (
            <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center gap-3">
                <Package className="h-5 w-5 text-gray-400" />
                <div className="min-w-0 whitespace-normal break-words">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {selectedJob.item_number}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    {selectedJob.title}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedJob(null);
                  setJobSearchQuery('');
                }}
                className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative" ref={jobDropdownRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search active jobs..."
                  value={jobSearchQuery}
                  onChange={(e) => {
                    setJobSearchQuery(e.target.value);
                    setShowJobSearch(true);
                  }}
                  onFocus={() => setShowJobSearch(true)}
                  className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
              {showJobSearch && (
                <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  {loadingJobs ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                      Loading jobs...
                    </div>
                  ) : filteredJobs.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      {jobSearchQuery.trim() ? 'No active jobs found' : 'No active jobs available'}
                    </div>
                  ) : (
                    <div>
                      {filteredJobs.map((job) => (
                        <button
                          key={job.id}
                          type="button"
                          onClick={() => {
                            setSelectedJob(job);
                            setShowJobSearch(false);
                            setJobSearchQuery('');
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <div className="font-medium text-gray-900 dark:text-white">
                            {job.item_number}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {job.title}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* No open jobs warning */}
      {selectedCustomer && jobsChecked && activeJobs.length === 0 && (
        <div className="mt-2 rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-700/50 dark:text-red-300">
          <AlertTriangle className="w-3.5 h-3.5" />
          No open jobs found for this contact.
        </div>
      )}
    </div>
  );
}

// ── Standalone Job Selector (used by LinkToAppointmentToggle) ────────────────
export interface JobSelectorProps {
  selectedJob: { id: string; item_number: string; title: string; contact_name?: string | null } | null;
  setSelectedJob: (j: { id: string; item_number: string; title: string; contact_name?: string | null } | null) => void;
  jobSearchQuery: string;
  setJobSearchQuery: (q: string) => void;
  showJobSearch: boolean;
  setShowJobSearch: (show: boolean) => void;
  loadingJobs: boolean;
  jobsChecked: boolean;
  filteredJobs: Job[];
  activeJobs: Job[];
  jobDropdownRef: React.RefObject<HTMLDivElement | null>;
}

export function JobSelector({
  selectedJob,
  setSelectedJob,
  jobSearchQuery,
  setJobSearchQuery,
  showJobSearch,
  setShowJobSearch,
  loadingJobs,
  jobsChecked,
  filteredJobs,
  activeJobs,
  jobDropdownRef,
}: JobSelectorProps) {
  return (
    <div className="space-y-2">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Active Job *
        </label>
        {selectedJob ? (
          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <Package className="h-5 w-5 text-gray-400" />
              <div className="min-w-0 whitespace-normal break-words">
                <div className="font-medium text-gray-900 dark:text-white">
                  {selectedJob.item_number}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {selectedJob.title}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedJob(null);
                setJobSearchQuery('');
              }}
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="relative" ref={jobDropdownRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search active jobs..."
                value={jobSearchQuery}
                onChange={(e) => {
                  setJobSearchQuery(e.target.value);
                  setShowJobSearch(true);
                }}
                onFocus={() => setShowJobSearch(true)}
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
            </div>
            {showJobSearch && (
              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                {loadingJobs ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                    Loading jobs...
                  </div>
                ) : filteredJobs.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    {jobSearchQuery.trim() ? 'No active jobs found' : 'No active jobs available'}
                  </div>
                ) : (
                  <div>
                    {filteredJobs.map((job) => (
                      <button
                        key={job.id}
                        type="button"
                        onClick={() => {
                          setSelectedJob(job);
                          setShowJobSearch(false);
                          setJobSearchQuery('');
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {job.item_number}
                        </div>
                        <div className="text-sm whitespace-normal break-words text-gray-700 dark:text-gray-300">
                          {job.title}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {jobsChecked && activeJobs.length === 0 && (
        <div className="rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-700/50 dark:text-red-300">
          <AlertTriangle className="w-3.5 h-3.5" />
          No open jobs found for this contact.
        </div>
      )}
    </div>
  );
}
